/* eslint-disable react/jsx-props-no-spreading */
import React from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import useFast from "../../../contexts/FASTContext";
import useFASTLayout from "../../../hooks/useFASTLayout";
import { getWeekDays } from "../../../utils/fast";
import Block from "../../block/Block";
import Input from "../../form/Input";
import NameHeaderInput from "../../form/NameHeaderInput";
import SaveButton from "../../form/SaveButton";
import ProgressBar from "../../utils/ProgressBar";

import type { JSX } from "react";

import type { GetTemplateResponse } from "../../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";
import type { PercentageItem } from "../../utils/ProgressBar";
import type { AllTemplateItems } from "../timeline/timeline.types";

interface TemplateSettingsFormProps {
  template?: GetTemplateResponse;
  items: AllTemplateItems[];
  onSubmit: (data: TemplateFormValues) => void;
}

export interface TemplateFormValues {
  name: string;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
}

function TemplateSettingsForm({
  template,
  onSubmit,
  items,
}: TemplateSettingsFormProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const { templateId } = useParams<
    FASTChannelRoutesParams & {
      templateId: string;
    }
  >();

  const isNewTemplate = templateId === "new";

  const methods = useForm<TemplateFormValues>({
    values: {
      name: isNewTemplate ? t("newTemplate") : (template?.name ?? ""),
      monday: template?.monday ?? false,
      tuesday: template?.tuesday ?? false,
      wednesday: template?.wednesday ?? false,
      thursday: template?.thursday ?? false,
      friday: template?.friday ?? false,
      saturday: template?.saturday ?? false,
      sunday: template?.sunday ?? false,
    },
  });
  const { state } = useFast();

  const { channelData } = state;

  const days = getWeekDays(channelData?.first_day_of_week);

  const getDailyCompletion = (): PercentageItem[] => {
    const dailyCompletion = items.reduce((acc, item) => acc + item.duration, 0);

    const millisecondsInADay = 86400000;

    return [{ value: (dailyCompletion / millisecondsInADay) * 100 }];
  };

  const formId = "template-settings-form";

  useFASTLayout({
    title: <NameHeaderInput control={methods.control} />,
    button: <SaveButton formId={formId} />,
    breadcrumbs: [
      {
        name: t("templating"),
        url: `/fast-channels/${channelData?.uuid}/templating`,
      },
      {
        name: isNewTemplate ? t("newTemplate") : (template?.name ?? ""),
      },
    ],
  });

  return (
    <div className="flex basis-1/4 flex-col gap-[0.78125rem]">
      <Block bodyStyle="p-[0.78125rem]!" title={t("dailyCompletion")}>
        <div className="bg-muted ring-border rounded-[0.3125rem] p-[0.625rem] ring-1">
          <ProgressBar percentageList={getDailyCompletion()} />
        </div>
      </Block>
      <Block isFullSize title={t("templateSettings")}>
        <FormProvider {...methods}>
          <form
            className="flex flex-col gap-[1.5625rem]"
            id={formId}
            onSubmit={methods.handleSubmit((data) => onSubmit(data))}
          >
            <div className="flex flex-col gap-[0.9375rem]">
              <div className="font-semibold">{t("associateTo")}</div>
              <div className="flex flex-col gap-[0.9375rem]">
                {days.map((day) => (
                  <div key={day} className="flex items-center gap-[0.9375rem]">
                    <Input
                      className="!mb-0 flex-[0]"
                      name={day}
                      type="checkbox"
                    />
                    <div className="flex">{t(day, { ns: "common" })}</div>
                  </div>
                ))}
              </div>
            </div>
          </form>
        </FormProvider>
      </Block>
    </div>
  );
}

export default TemplateSettingsForm;
